import { useEffect, useState } from 'react';
import { startOfWeek, endOfWeek } from '@aelita/shared';
import { useAuthStore } from '@/stores/auth';
import { useTimeEntries } from './useTimeEntries';

type PersonalTimeQuery = Readonly<
  Pick<ReturnType<typeof useTimeEntries>, 'data' | 'error' | 'isPending' | 'isError'>
>;

export function usePersonalTimeEntries() {
  const { user, isAuthenticated } = useAuthStore();
  const employeeId = isAuthenticated ? user?.id : undefined;
  const hasIdentity = Boolean(employeeId);
  const [snapshot, setSnapshot] = useState(() => new Date());

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const refresh = () => {
      const now = new Date();
      setSnapshot(now);
      clearTimeout(timeout);
      const nextMonday = startOfWeek(now);
      nextMonday.setDate(nextMonday.getDate() + 7);
      timeout = setTimeout(refresh, nextMonday.getTime() - now.getTime());
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const list = useTimeEntries({ employeeId, limit: 50 }, hasIdentity);
  const week = useTimeEntries(
    {
      employeeId,
      limit: 1,
      startDate: startOfWeek(snapshot).toISOString(),
      endDate: endOfWeek(snapshot).toISOString(),
    },
    hasIdentity,
  );
  const pending = useTimeEntries({ employeeId, limit: 1, isApproved: false }, hasIdentity);
  const mask = (query: typeof list): PersonalTimeQuery => ({
    data: hasIdentity ? query.data : undefined,
    error: hasIdentity ? query.error : null,
    isPending: hasIdentity && query.isPending,
    isError: hasIdentity && query.isError,
  });

  return { list: mask(list), week: mask(week), pending: mask(pending), hasIdentity };
}
